import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Question1.css";
import { East } from "@mui/icons-material";
import BlurText from "../../utils/BlurText";
import API_URL from "../../utils/API_URL";

// --- Validators ---
function validateEmail(email) {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()\[\]\\.,;:\s@"]+\.)+[^<>()\[\]\\.,;:\s@"]{2,})$/i;
  return re.test(String(email).toLowerCase());
}
function validatePhone(phone) {
  return /^\+?\d{8,15}$/.test(String(phone).replace(/[\s\-]/g, ""));
}
function isEmpty(val) {
  return (
    val === undefined ||
    val === null ||
    (typeof val === "string" && val.trim() === "") ||
    (Array.isArray(val) && val.length === 0)
  );
}

// ------------------------------------------------------

const QUESTIONS = [
  {
    key: "role",
    label: "Role",
    blurPrompt:
      "To tailor what you’ll see, tell me how you’d best describe yourself:",
    type: "select",
    options: [
      "Brand / Retailer",
      "Creator / Designer",
      "Partner / Platform",
      { label: "Other", other: true },
      "Just browsing → homepage",
    ],
  },
  {
    key: "brand_focus",
    label: "Brand Focus",
    blurPrompt:
      "Brands often come here looking to solve specific challenges. Which area feels most relevant to you right now?",
    type: "select",
    options: [
      "Sizing & Fit",
      "Immersive Experiences & Gaming Elements",
      "Scaling Brand",
      { label: "Other", other: true },
    ],
    conditional: { role: "Brand / Retailer" },
  },
  {
    key: "creator_focus",
    label: "Creator Focus",
    blurPrompt:
      "Creators usually explore new ways to bring ideas to life. What are you curious about?",
    type: "select",
    options: [
      "Phygital Drops",
      "Digital Fashion",
      "Collaboration Opportunities",
      { label: "Other", other: true },
    ],
    conditional: { role: "Creator / Designer" },
  },
  {
    key: "partner_focus",
    label: "Partner Focus",
    blurPrompt:
      "Partnerships come in many forms. What are you most interested in?",
    type: "select",
    options: [
      "Tech Integration",
      "Co-Branded Storytelling / Case Studies",
      "Pilot Testing Together",
      { label: "Other", other: true },
    ],
    conditional: { role: "Partner / Platform" },
  },
  {
    key: "timeline",
    label: "Timeline",
    blurPrompt: "When do you see yourself exploring solutions like this?",
    type: "select",
    options: [
      "Just browsing",
      "3–6 months",
      "Already exploring solutions",
      { label: "Other", other: true },
    ],
  },
  {
    key: "profileForm",
    label: "Profile Info",
    blurPrompt: "Finally, share a few details about yourself:",
    type: "form",
    fields: [
      {
        key: "firstName",
        label: "First Name",
        placeholder: "Enter your first name",
        required: true,
      },
      {
        key: "lastName",
        label: "Last Name",
        placeholder: "Enter your last name",
        required: true,
      },
      {
        key: "email",
        label: "Work Email (Optional)",
        placeholder: "yourname@company.com",
      },
      {
        key: "company",
        label: "Company / Brand (Optional)",
        placeholder: "Enter your company name",
      },
      {
        key: "phone",
        label: "Phone Number (Optional)",
        placeholder: "+1 234 567 8901",
      },
    ],
  },
];

// ------------------------------------------------------

function QuestionInput({ question, value, setValue, onSubmit }) {
  if (question.type === "select") {
    const selectedIsOther = value && value.__other;

    return (
      <>
        <div className="onboard-q1-chips">
          {question.options.map((opt) => {
            const isObj = typeof opt === "object";
            const label = isObj ? opt.label : opt;
            const isOther = isObj && opt.other;

            return (
              <button
                key={label}
                type="button"
                className={`onboard-chip ${
                  !selectedIsOther && value === label ? "selected" : ""
                }`}
                onClick={() => {
                  if (isOther) {
                    setValue({ __other: true, text: "" });
                  } else if (label === "Just browsing → homepage") {
                    // open homepage in a new tab when clicking the "Just browsing" chip
                    window.open("https://my.livvium.com/", "_blank", "noopener,noreferrer");
                  }
                  else if (label === "Just browsing") {
                    // open homepage in a new tab when clicking the "Just browsing" chip
                    window.open("https://my.livvium.com/", "_blank", "noopener,noreferrer");
                  } else {
                    setValue(label);
                    onSubmit(label);
                  }
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Show "Other" textbox OUTSIDE chip row */}
        {selectedIsOther && (
          <div className="onboard-q1-input-block">
            <input
              type="text"
              placeholder="Type your answer..."
              className="onboard-q1-input-without-border enhanced"
              value={value.text}
              onChange={(e) =>
                setValue({ __other: true, text: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && value.text.trim()) {
                  e.preventDefault();
                  onSubmit(value.text);
                }
              }}
            />
            <button
              type="button"
              className="onboard-q1-send-btn"
              onClick={() => {
                if (value.text.trim()) onSubmit(value.text);
              }}
            >
              <East />
            </button>
          </div>
        )}
      </>
    );
  }
  if (question.type === "form") {
    return (
      <form
        className="onboard-q1-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        {question.fields.map((field) => (
          <div key={field.key} className="onboard-q1-form-field">
            <label className="onboard-q1-label">{field.label}</label>
            <input
              type="text"
              placeholder={field.placeholder}
              className="onboard-q1-input enhanced"
              value={value[field.key] || ""}
              onChange={(e) =>
                setValue({
                  ...value,
                  [field.key]: e.target.value,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onSubmit();
                }
              }}
            />
          </div>
        ))}
      </form>
    );
  }
  return null;
}

// ------------------------------------------------------

export default function Question1() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const getVisibleQuestions = () => {
    return QUESTIONS.filter((q) => {
      if (!q.conditional) return true;
      return Object.entries(q.conditional).every(
        ([depKey, depVal]) => answers[depKey] === depVal
      );
    });
  };

  const visibleQuestions = getVisibleQuestions();
  const currQ = visibleQuestions[step];
  const currValue =
    currQ.type === "multi-select"
      ? Array.isArray(answers[currQ.key])
        ? answers[currQ.key]
        : []
      : answers[currQ.key] !== undefined
      ? answers[currQ.key]
      : input;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [step, answers]);

  // --- Per-step submit ---
  const handleSubmit = (val) => {
    let value = val !== undefined ? val : input;

    if (isEmpty(value)) {
      toast.error("This field is required.");
      return;
    }

    if (currQ.key === "email" && value && !validateEmail(value)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (currQ.key === "phone" && value && !validatePhone(value)) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setAnswers((prev) => ({ ...prev, [currQ.key]: value }));
    setInput("");
    if (step < visibleQuestions.length - 1) {
      setStep((prev) => prev + 1);
    }
  };

  // --- Final Submit ---
  const handleFinalSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    const finalAnswers = { ...answers };
    if (currQ.type === "form") {
      Object.assign(finalAnswers, currValue);
    }

    for (let q of visibleQuestions) {
      if (q.type === "form") {
        for (let f of q.fields) {
          const val = finalAnswers[f.key];
          if (f.required && isEmpty(val)) {
            toast.error(`"${f.label}" is required.`);
            setLoading(false);
            return;
          }
          if (f.key === "email" && val && !validateEmail(val)) {
            toast.error("Please enter a valid email address.");
            setLoading(false);
            return;
          }
          if (f.key === "phone" && val && !validatePhone(val)) {
            toast.error("Please enter a valid phone number.");
            setLoading(false);
            return;
          }
        }
      }
    }

    try {
      const allAnswers = {
        ...finalAnswers,
        ph_no: localStorage.getItem("ph_no"),
      };
      const res = await axios.post(
        `${API_URL}/preferences/savePreference`,
        allAnswers
      );
      if (res.status === 200) {
        toast.success("Thanks for sharing. You’re all set to explore LIVVIUM. Inside, you’ll find the full O.Li agent, always in the bottom-right corner — ready to guide you in real time.");
        localStorage.setItem("formSubmitted", "true");
        setSubmitted(true);
      } else {
        toast.error(res.data?.message || "Failed to save preferences.");
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 409) {
          navigate("/preference");
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error("Network error. Please try later.");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- UI ---
  return (
    <div className="onboard-q1-container">
      {submitted || localStorage.getItem("formSubmitted") === "true" ? (
        <div className="onboard-popup-overlay">
          <div className="onboard-popup-box">
            <h3>Submission Sent!</h3>
            <p>
              Thank you for completing your profile. Our team will reach out
              soon!
            </p>
            <div className="onboard-popup-actions">
              <button
                className="onboard-popup-btn"
                onClick={() => {
                  setSubmitted(false);
                  window.location.replace("https://my.livvium.com/");
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="onboard-q1-header" />
      <div
        className="onboard-q1-skip"
        onClick={() => window.location.replace("https://my.livvium.com/")}
      >
        Skip
      </div>

      {/* Progress */}
      <div className="onboard-q1-progress-bar-outer">
        <div
          className="onboard-q1-progress-bar-inner"
          style={{
            width: `${Math.round((step / visibleQuestions.length) * 100)}%`,
          }}
        />
      </div>

      {/* Main Content */}
      <div className="onboard-q1-content-container">
        <div className="question1" ref={scrollRef}>
          {/* Intro */}
          <div
            className="question1_container_animation"
            style={{ marginBottom: 16, textAlign: "start" }}
          >
            {step === 0 ? (
              <BlurText
                text={
                  "Welcome to LIVVIUM! I’ll be your retail innovation partner 👋 \nBefore we craft your tailored journey, let’s build your brand profile." +
                  "\n\n" +
                  currQ.blurPrompt
                }
                delay={70}
                animateBy="words"
                direction="top"
                className="text-2xl mb-8"
              />
            ) : (
              <div className="text-2xl mb-8">{currQ.blurPrompt}</div>
            )}
          </div>

          {/* Question */}
          {(currQ.type === "select" || currQ.type === "multi-select") && (
            <QuestionInput
              question={currQ}
              value={answers[currQ.key] || ""}
              setValue={(val) =>
                setAnswers((prev) => ({
                  ...prev,
                  [currQ.key]: val,
                }))
              }
              onSubmit={handleSubmit}
            />
          )}

          {(currQ.type === "text" || currQ.type === "textarea") && (
            <div className="onboard-q1-input-block">
              <QuestionInput
                question={currQ}
                value={currValue}
                setValue={setInput}
                onSubmit={handleSubmit}
              />
              <button
                type="button"
                className="onboard-q1-next-btn"
                onClick={() => handleSubmit()}
                disabled={
                  !currValue ||
                  (typeof currValue === "string" && currValue.trim() === "")
                }
              >
                Continue <East />
              </button>
            </div>
          )}

          {currQ.type === "form" && (
            <>
              <QuestionInput
                question={currQ}
                value={answers[currQ.key] || {}}
                setValue={(val) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [currQ.key]: val,
                  }))
                }
                onSubmit={handleFinalSubmit}
              />
              <button
                type="button"
                className="onboard-q1-next-btn-submit-btn"
                onClick={handleFinalSubmit}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
